import { downloadBlob } from '@racemap/utilities/functions/download';
import type { RacemapGeoObject } from '@racemap/utilities/types/geos';
import type { Feature } from 'geojson';
import type { Immutable } from 'immer';
import mapboxgl, { type LngLatLike } from 'mapbox-gl';
import type { ViewState } from 'react-map-gl/mapbox';

export function getFeatureById<T extends RacemapGeoObject>(
  id: string | null,
  features: Immutable<Array<T>>,
): T | null | undefined {
  if (id == null) return null;

  return features.find((f) => f.id === id) as T | undefined;
}

export const exportFeature = (feature: Feature) => {
  const filename = feature.properties?.name || 'feature';
  const blob = new Blob([JSON.stringify(feature)], {
    type: 'application/vnd.geo+json',
  });
  downloadBlob(blob, `${filename}_track.geojson`);
};

type FeatureLike = { geometry: { coordinates?: ReadonlyArray<unknown> } };

/** Computes a LngLatBounds that encloses all vertices of the given features.
 * Recurses into coordinates at any nesting depth, so Point/LineString/Polygon/Multi* all work. */
export function boundsForFeatures(features: ReadonlyArray<FeatureLike>): mapboxgl.LngLatBounds {
  const bounds = new mapboxgl.LngLatBounds();
  const extend = (coordinates: ReadonlyArray<unknown>): void => {
    if (coordinates.length === 0) return;
    if (typeof coordinates[0] === 'number') {
      const [lng, lat] = coordinates as ReadonlyArray<number>;
      if (typeof lng === 'number' && typeof lat === 'number') {
        bounds.extend([lng, lat] as LngLatLike);
      }
    } else {
      for (const c of coordinates as ReadonlyArray<ReadonlyArray<unknown>>) extend(c);
    }
  };
  for (const f of features) {
    if (f.geometry.coordinates != null) extend(f.geometry.coordinates);
  }
  return bounds;
}

/** Flies the map to show all features. Zooms out only if the current zoom already fits; never zooms in. */
export function fitMapToFeatures(map: mapboxgl.Map, features: ReadonlyArray<FeatureLike>): void {
  const bounds = boundsForFeatures(features);
  if (bounds.isEmpty()) return;
  const { width, height } = map.getContainer().getBoundingClientRect();
  const padding = Math.min(100, Math.min(width, height) * 0.1);
  const camera = map.cameraForBounds(bounds, { padding });

  const fitZoom = camera?.zoom;
  const center = bounds.getCenter();
  if (fitZoom != null && fitZoom < map.getZoom()) {
    map.flyTo({ center: camera?.center ?? center, zoom: fitZoom });
  } else {
    map.flyTo({ center });
  }
}

export function getDefaultViewState(withElevationChart = false): ViewState {
  return {
    longitude: 1.0,
    latitude: 1.0,
    zoom: 0.44,
    pitch: 0,
    bearing: 0,
    padding: {
      top: 100,
      bottom: withElevationChart ? 200 : 100,
      left: 100,
      right: 100,
    },
  };
}
